import React, { useState, useEffect } from "react";
import {
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Button,
  Group,
  Stack,
  LoadingOverlay,
  Switch,
  Text,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { DepositFormData, SalonService } from "../../types/deposit";
import { validateDepositForm } from "../../utils/depositUtils";
import ServiceSelection from "./ServiceSelection";

interface DepositFormProps {
  initialData?: DepositFormData;
  services: SalonService[];
  onSubmit: (data: DepositFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const DepositForm = ({
  initialData,
  services,
  onSubmit,
  onCancel,
  isLoading = false,
}: DepositFormProps) => {
  const [formData, setFormData] = useState<DepositFormData>({
    name: "",
    description: "",
    type: "Fixed Amount",
    amount: 0,
    salon_service_ids: [],
    is_active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleInputChange = (field: keyof DepositFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateDepositForm(formData);
    setErrors(validation.errors);

    if (validation.isValid) {
      onSubmit(formData);
    }
  };

  const handleServiceSelectionChange = (serviceIds: string[]) => {
    handleInputChange("salon_service_ids", serviceIds);
    if (errors.services) {
      setErrors((prev) => ({ ...prev, services: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <LoadingOverlay visible={isLoading} />
      <Stack gap="md">
        <TextInput
          label="Name"
          required
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          error={errors.name}
          placeholder="e.g., 50% Deposit"
        />

        <Textarea
          label="Description (Optional)"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder="Describe when this deposit applies..."
          rows={3}
        />

        <Group grow>
          <Select
            label="Type"
            required
            value={formData.type}
            onChange={(value) => handleInputChange("type", value)}
            data={[
              { value: "Fixed Amount", label: "Fixed Amount" },
              { value: "Percentage", label: "Percentage" },
            ]}
            error={errors.type}
          />

          <NumberInput
            label="Amount"
            required
            value={formData.amount}
            onChange={(value) => handleInputChange("amount", value)}
            min={0}
            max={formData.type === "Percentage" ? 100 : undefined}
            decimalScale={formData.type === "Fixed Amount" ? 2 : 0}
            prefix={formData.type === "Fixed Amount" ? "$" : undefined}
            suffix={formData.type === "Percentage" ? "%" : undefined}
            error={errors.amount}
          />
        </Group>

        <ServiceSelection
          services={services}
          selectedServiceIds={formData.salon_service_ids}
          onSelectionChange={handleServiceSelectionChange}
          error={errors.services}
        />

        {initialData && (
          <Switch
            label="Active"
            description="Inactive deposit configurations will not be applied to new bookings"
            checked={formData.is_active}
            onChange={(event) =>
              handleInputChange("is_active", event.currentTarget.checked)
            }
            color="teal"
          />
        )}

        <Group justify="flex-end" gap="sm">
          <Button
            variant="light"
            color="gray"
            onClick={onCancel}
            leftSection={<IconX size={16} />}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="teal"
            leftSection={<IconCheck size={16} />}
          >
            {initialData ? "Update" : "Create"} Deposit
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default DepositForm;
